import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useMemo } from "react";

/**
 * Utility that creates a hook for managing react query cache in a typesafe manner..
 *
 * @argument useKey - A function that generates a query key based on the provided arguments.
 *
 * @returns A function that, when called with the arguments for the query key function,
 * returns an object with the following methods:
 * - `queryKey`: The generated query key.
 * - `get`: Retrieves the current data associated with the query key.
 * - `set`: Updates the data associated with the query key. Accepts either a new value or an updater function.
 * - `invalidate`: Invalidates the queries associated with the query key, triggering a refetch.
 * - `cancel`: Cancels any ongoing queries associated with the query key.
 */
export const createEntityQueryHook =
  <T, A extends unknown[] = []>(useKey: (...args: A) => QueryKey) =>
  (...args: A) => {
    const client = useQueryClient();
    const queryKey = useKey(...args);
    return {
      queryKey,
      get: () => client.getQueryData<T>(queryKey),
      set: (x: T | ((x?: T) => T | undefined)) => client.setQueryData(queryKey, x),
      invalidate: () => client.invalidateQueries({ queryKey }),
      cancel: () => client.cancelQueries({ queryKey }),
    };
  };

export const useQueryCache = <T>(key: QueryKey) => {
  const client = useQueryClient();
  return useMemo(
    () => ({
      get: () => client.getQueryData<T>(key),
      set: (x: T | ((x?: T) => T | undefined)) => client.setQueryData(key, x),
    }),
    [client, key],
  );
};
